import React, { useState } from 'react';
import styled from 'astroturf/react';
import { SuggestionReason } from 'js/social/suggestions/SuggestionReason';
import AvatarBase from 'js/social/shared/Avatar';
import { Body as BodyBase } from 'js/design_system/Body';
import { FollowActionButton as FollowActionButtonBase } from 'js/social/shared/FollowActionButton';
import { createUserFollowing } from 'js/social/api/user_following_api';
import { SuggestionMetadata } from 'js/social/shared/types';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

export type FollowSuggestion = {
  id: number;
  displayName?: string | null;
  photoUrl: string;
  universityName?: string;
  departmentName?: string;
  suggestionReason?: {
    sources: Array<string>;
    metadata: SuggestionMetadata;
  };
  following?: boolean;
};

type Props = {
  followableUser: FollowSuggestion;
  onFollow: () => void;
  index: number;
  followingSource: string;
  impressionSource?: string; // If present, log impressions of follow buttons
  topic?: {
    name: string;
    score?: number;
  };
  following?: boolean;
};

const FollowActionButton = styled(FollowActionButtonBase)`
  margin: 0;
  width: 100%;
`;

const SuggestionReasonContainer = styled.div`
  margin-left: 54px;
  margin-bottom: 20px;

  @include for-tablet-up {
    margin-bottom: 0;
  }
`;

const ProfileRow = styled.div`
  display: flex;
  gap: 8px;
`;

const FollowSuggestionItem = styled.div<{ suggested: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 12px 0px;
  gap: 8px;

  &.suggested {
    &:not(:last-child) {
      border-bottom: 1px solid $ds-gray-light;
    }
  }
  @include for-tablet-up {
    flex-direction: row;
    padding: 12px;
    gap: 32px;
  }
`;

const Avatar = styled(AvatarBase)`
  --size: 46px;
  flex-shrink: 0;
`;

const ProfileText = styled.div`
  flex-grow: 1;
  color: $ds-black;
`;

const SuggestionContainer = styled.div`
  flex: 1;
`;

const UserInfo = styled(BodyBase)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BulkFollowSuggestion = ({
  followableUser,
  onFollow,
  index,
  followingSource,
  impressionSource = '',
  topic,
  following = false,
}: Props) => {
  const [follows, setFollows] = useState(following);
  const followSourceDetail =
    followableUser.suggestionReason !== undefined
      ? {
          suggestion_reason: followableUser.suggestionReason.sources[0],
        }
      : topic !== undefined
        ? {
            topic: topic?.name ?? '',
            topic_score: topic?.score ?? 0,
            individual_follow: 'true',
          }
        : {};
  const follow = async () => {
    await createUserFollowing(
      followableUser.id,
      followingSource,
      followSourceDetail,
    );
    setFollows(true);
    onFollow();
  };

  return (
    <FollowSuggestionItem
      suggested={followableUser.suggestionReason !== undefined}
    >
      <SuggestionContainer>
        <ProfileRow>
          <Avatar
            fullName={followableUser?.displayName || ''}
            photoUrl={followableUser.photoUrl}
          />

          <ProfileText>
            <UserInfo bold>{followableUser.displayName}</UserInfo>
            {followableUser.universityName && (
              <UserInfo size="sm">{followableUser.universityName}</UserInfo>
            )}
            {followableUser.departmentName && (
              <UserInfo size="sm">{followableUser.departmentName}</UserInfo>
            )}
          </ProfileText>
        </ProfileRow>
        {followableUser.suggestionReason && (
          <SuggestionReasonContainer>
            <SuggestionReason
              source={followableUser.suggestionReason.sources[0]}
              metadata={followableUser.suggestionReason.metadata}
              style={{ margin: 0 }}
            />
          </SuggestionReasonContainer>
        )}
      </SuggestionContainer>

      <ImpressionWatcher
        active={!!impressionSource}
        source={impressionSource}
        extra={{
          modalIdx: index,
          ...followSourceDetail,
        }}
      >
        <FollowActionButton
          size="xs"
          variant="inverse"
          userId={followableUser.id}
          follows={follows}
          onFollow={follow}
          alwaysShowFollowButton
        >
          Follow
        </FollowActionButton>
      </ImpressionWatcher>
    </FollowSuggestionItem>
  );
};

export default BulkFollowSuggestion;
